import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import NotFound from "../ul/NotFound";

const UserChart = ({ userData = [] }) => {
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const countByDate = (data) =>
    data.reduce((acc, item) => {
      const dateValue = item.createdAt;
      if (dateValue) {
        const date = formatDate(dateValue);
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    }, {});

  const newUserCounts = useMemo(() => countByDate(userData), [userData]);
  const allDates = useMemo(
    () =>
      Object.keys(newUserCounts).sort(
        (a, b) =>
          new Date(a.split("/").reverse().join("-")) -
          new Date(b.split("/").reverse().join("-"))
      ),
    [newUserCounts]
  );
  const newUserValues = allDates.map((date) => newUserCounts[date] || 0);
  const hasData = userData.length > 0;

  const chartOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#1e293b", fontSize: 13, fontWeight: 500 },
      padding: [12, 16],
      borderRadius: 12,
      extraCssText: "box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);",
      axisPointer: {
        type: "shadow",
        shadowStyle: {
          color: "rgba(139,92,246,0.1)",
        },
      },
      formatter: function (params) {
        const param = params[0];
        return `<div style="font-weight:600;font-size:14px;">${param.name}</div>
          <div>
          <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#8b5cf6;margin-right:6px;"></span>
          New Users: <b>${param.value}</b>
          </div>`;
      },
    },
    legend: {
      data: ["New Users"],
      top: 20,
      left: 24,
      icon: "roundRect",
      itemWidth: 16,
      itemHeight: 10,
      itemGap: 24,
      textStyle: { color: "#475569", fontWeight: 600, fontSize: 13 },
    },
    grid: {
      left: "3%",
      right: "3%",
      bottom: "10%",
      top: "18%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: allDates,
      axisLine: { lineStyle: { color: "#e2e8f0", width: 2 } },
      axisTick: { show: false },
      axisLabel: {
        color: "#64748b",
        fontWeight: 500,
        fontSize: 12,
        margin: 12,
      },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      splitLine: { lineStyle: { color: "#f1f5f9", width: 1.5 } },
      axisLabel: { color: "#64748b", fontSize: 12, fontWeight: 500 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        name: "New Users",
        type: "bar",
        barWidth: 24,
        data: newUserValues,
        itemStyle: {
          color: "#8b5cf6",
          shadowColor: "rgba(139, 92, 246, 0.3)",
          shadowBlur: 10,
          borderRadius: [6, 6, 0, 0],
        },
        emphasis: {
          itemStyle: {
            borderColor: "#8b5cf6",
            borderWidth: 3,
            shadowColor: "rgba(139, 92, 246, 0.5)",
            shadowBlur: 20,
          },
        },
        label: {
          show: true,
          position: "top",
          color: "#5b21b6",
          fontWeight: 600,
          fontSize: 13,
        },
      },
    ],
  };

  // Summary calculations
  const totalUsers = newUserValues.reduce((a, b) => a + b, 0);

  return (
    <div
      className="bg-white p-6 md:p-8 rounded-2xl w-full mx-auto border border-slate-200"
      style={{
        boxShadow:
          "0 4px 16px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-7 bg-gradient-to-b from-purple-500 to-amber-500 rounded-full" />
          <h3 className="text-lg md:text-xl font-bold text-slate-800">
            User Analytics Overview
          </h3>
        </div>
        {hasData && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            {allDates.length} {allDates.length === 1 ? "day" : "days"}
          </span>
        )}
      </div>
      {!hasData ? (
        <div className="flex items-center justify-center h-64">
          <NotFound />
        </div>
      ) : (
        <>
          <div className="relative w-full h-[400px] md:h-[450px]">
            <ReactECharts
              option={chartOption}
              style={{ height: "100%", width: "100%" }}
              notMerge
              lazyUpdate
              theme={null}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UserChart;
